import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiGet, adminCompanyPath } from '../../lib/api-client';
import { useCompany } from '../../contexts/CompanyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

export function AdminDashboard() {
  const { t } = useTranslation();
  const { activeCompanyId, activeCompany } = useCompany();

  const { data: sellersData } = useQuery({
    queryKey: ['admin', 'sellers', activeCompanyId],
    queryFn: () => apiGet<{ items: unknown[]; total: number }>(adminCompanyPath('/sellers', activeCompanyId!)),
    enabled: !!activeCompanyId,
  });

  const { data: configData } = useQuery({
    queryKey: ['admin', 'api-config', activeCompanyId],
    queryFn: () =>
      apiGet<{ lastSyncAt: string | null; apiUrl: string } | null>(
        adminCompanyPath('/api-config', activeCompanyId!),
      ),
    enabled: !!activeCompanyId,
  });

  if (!activeCompanyId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('dashboard.title')}</h1>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          {t('company.noCompanySelected')}
        </div>
      </div>
    );
  }

  const sellerCount = sellersData?.ok ? sellersData.data.total : 0;
  const lastSync = configData?.ok && configData.data ? configData.data.lastSyncAt : null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('dashboard.title')}</h1>
      {activeCompany && (
        <p className="text-gray-500 dark:text-gray-400">
          {activeCompany.name} <span className="font-mono text-xs text-gray-400">({activeCompany.code})</span>
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader><CardTitle>{t('dashboard.activeSellers')}</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{sellerCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t('admin.lastSync')}</CardTitle></CardHeader>
          <CardContent>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {lastSync ? new Date(lastSync).toLocaleString() : t('admin.neverSynced')}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t('admin.apiStatus')}</CardTitle></CardHeader>
          <CardContent>
            <div className="text-sm">
              {configData?.ok && configData.data ? (
                <span className="text-green-600">{t('admin.configured')}</span>
              ) : (
                <span className="text-yellow-600">{t('admin.notConfigured')}</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
